import { z } from 'zod';
import type { FormField } from './types';

/**
 * Builds a runtime Zod schema from an array of FormField definitions.
 *
 * Returns a `z.ZodObject` whose keys are field IDs.
 * Called in preview mode before each form submission to validate the
 * flat `{ [fieldId]: value }` data object.
 *
 * Pure function — no side effects.
 */
export function buildFormSchema(fields: FormField[]): z.ZodObject<z.ZodRawShape> {
  const shape: z.ZodRawShape = {};

  for (const field of fields) {
    let schema: z.ZodTypeAny;

    switch (field.type) {
      case 'text': {
        if (field.required) {
          schema = z.string().min(1, `${field.label} is required`);
        } else {
          schema = z.string().optional();
        }
        break;
      }

      case 'number': {
        let n = z.coerce.number({ invalid_type_error: `${field.label} must be a number` });
        if (field.min !== undefined) {
          n = n.min(field.min, `${field.label} must be at least ${field.min}`);
        }
        if (field.max !== undefined) {
          n = n.max(field.max, `${field.label} must be at most ${field.max}`);
        }
        schema = field.required ? n : n.optional();
        break;
      }

      case 'select': {
        if (field.options && field.options.length > 0) {
          const enumSchema = z.enum(field.options as [string, ...string[]]);
          schema = field.required ? enumSchema : enumSchema.optional();
        } else {
          schema = field.required
            ? z.string().min(1, `${field.label} is required`)
            : z.string().optional();
        }
        break;
      }

      case 'multiselect': {
        if (field.options && field.options.length > 0) {
          const arr = z.array(z.enum(field.options as [string, ...string[]]));
          schema = field.required
            ? arr.min(1, `${field.label} requires at least one selection`)
            : arr.optional();
        } else {
          const arr = z.array(z.string());
          schema = field.required
            ? arr.min(1, `${field.label} requires at least one selection`)
            : arr.optional();
        }
        break;
      }

      case 'file': {
        if (typeof FileList !== 'undefined') {
          const fl = z.instanceof(FileList);
          schema = field.required
            ? fl.refine((list: FileList) => list.length > 0, `${field.label} is required`)
            : fl.optional();
        } else {
          schema = z.unknown();
        }
        break;
      }

      default: {
        schema = z.unknown();
      }
    }

    shape[field.id] = schema;
  }

  return z.object(shape);
}
